import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { proposalsService } from '../../services';
import { logger } from '@/utils/logger';

// Async thunks
export const fetchProposals = createAsyncThunk(
  'proposals/fetchProposals',
  async ({ filters = {}, userId = null } = {}, { rejectWithValue }) => {
    try {
      const { data, error } = await proposalsService.getProposals(filters);
      if (error) throw new Error(error);
      return { proposals: data || [], userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProposalById = createAsyncThunk(
  'proposals/fetchProposalById',
  async (proposalId, { rejectWithValue }) => {
    try {
      const { data, error } = await proposalsService.getProposalById(proposalId);
      if (error) throw new Error(error);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProposal = createAsyncThunk(
  'proposals/createProposal',
  async (proposalData, { rejectWithValue }) => {
    try {
      const { data, error } = await proposalsService.createProposal(proposalData);
      if (error) throw new Error(error);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleProposalVote = createAsyncThunk(
  'proposals/toggleProposalVote',
  async ({ userId, proposalId }, { rejectWithValue }) => {
    try {
      const { voted, error } = await proposalsService.toggleProposalVote(userId, proposalId);
      if (error) throw new Error(error);
      return { proposalId, voted, userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkUserVoted = createAsyncThunk(
  'proposals/checkUserVoted',
  async ({ userId, proposalId }, { rejectWithValue }) => {
    try {
      const { hasVoted, error } = await proposalsService.hasUserVoted(userId, proposalId);
      if (error) throw new Error(error);
      return { proposalId, hasVoted };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserProposals = createAsyncThunk(
  'proposals/fetchUserProposals',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await proposalsService.getUserProposals(userId);
      if (error) throw new Error(error);
      return data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  items: [],
  selected_proposal: null,
  user_proposals: [],
  user_votes: {}, // Track user votes for proposals
  loading: false,
  selectedLoading: false,
  user_proposals_loading: false,
  error: null,
  categories: ['bug', 'improvement', 'feedback'], // From DB enum
  statuses: ['open', 'in_progress', 'completed', 'closed'] // From DB enum
};

export const proposalsSlice = createSlice({
  name: 'proposals',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedProposal: (state) => {
      state.selected_proposal = null;
    },
    setUserVote: (state, action) => {
      const { proposalId, hasVoted } = action.payload;
      state.user_votes[proposalId] = hasVoted;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch proposals
      .addCase(fetchProposals.pending, (state) => {
        logger.debug('ProposalsSlice', 'fetchProposals pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProposals.fulfilled, (state, action) => {
        logger.info('ProposalsSlice', 'fetchProposals fulfilled', { count: action.payload.proposals?.length });
        state.loading = false;
        const { proposals, userId } = action.payload;
        state.items = proposals;
        
        // Initialize user_votes for all proposals based on current data
        if (userId) {
          proposals.forEach(proposal => {
            // Check if user has voted by looking at the proposal_votes array
            const userHasVoted = proposal.proposal_votes && proposal.proposal_votes.some(vote => vote.user_id === userId);
            state.user_votes[proposal.id] = userHasVoted || false;
          });
        }
      })
      .addCase(fetchProposals.rejected, (state, action) => {
        logger.error('ProposalsSlice', 'fetchProposals rejected', { error: action.payload });
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch proposal by ID
      .addCase(fetchProposalById.pending, (state) => {
        state.selectedLoading = true;
        state.error = null;
      })
      .addCase(fetchProposalById.fulfilled, (state, action) => {
        state.selectedLoading = false;
        state.selected_proposal = action.payload;
      })
      .addCase(fetchProposalById.rejected, (state, action) => {
        state.selectedLoading = false;
        state.error = action.payload;
      })
      
      // Create proposal
      .addCase(createProposal.pending, (state) => {
        logger.debug('ProposalsSlice', 'createProposal pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(createProposal.fulfilled, (state, action) => {
        logger.info('ProposalsSlice', 'createProposal fulfilled', { proposal_id: action.payload?.id });
        state.loading = false;
        const newProposal = action.payload;

        // Initialize vote tracking for the new proposal
        // The creator automatically votes for their own proposal
        state.user_votes[newProposal.id] = true; // Creator has voted

        state.items.unshift(newProposal); // Add to beginning
      })
      .addCase(createProposal.rejected, (state, action) => {
        logger.error('ProposalsSlice', 'createProposal rejected', { error: action.payload });
        state.loading = false;
        state.error = action.payload;
      })
      
      // Toggle proposal vote
      .addCase(toggleProposalVote.fulfilled, (state, action) => {
        const { proposalId, voted, userId } = action.payload;
        
        // Update user vote tracking
        state.user_votes[proposalId] = voted;
        
        // Update vote count in items list
        const proposal = state.items.find(item => item.id === proposalId);
        if (proposal) {
          if (voted) {
            // Add user vote
            proposal.proposal_votes = proposal.proposal_votes ? [...proposal.proposal_votes, { user_id: userId }] : [{ user_id: userId }];
          } else {
            // Remove user vote
            proposal.proposal_votes = proposal.proposal_votes ? proposal.proposal_votes.filter(vote => vote.user_id !== userId) : [];
          }
        }
        
        // Update vote count in selected proposal
        if (state.selected_proposal && state.selected_proposal.id === proposalId) {
          if (voted) {
            state.selected_proposal.proposal_votes = state.selected_proposal.proposal_votes ? [...state.selected_proposal.proposal_votes, { user_id: userId }] : [{ user_id: userId }];
          } else {
            state.selected_proposal.proposal_votes = state.selected_proposal.proposal_votes ? state.selected_proposal.proposal_votes.filter(vote => vote.user_id !== userId) : [];
          }
        }
      })
      .addCase(toggleProposalVote.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Check user voted
      .addCase(checkUserVoted.fulfilled, (state, action) => {
        const { proposalId, hasVoted } = action.payload;
        state.user_votes[proposalId] = hasVoted;
      })
      
      // Fetch user proposals
      .addCase(fetchUserProposals.pending, (state) => {
        state.user_proposals_loading = true;
        state.error = null;
      })
      .addCase(fetchUserProposals.fulfilled, (state, action) => {
        state.user_proposals_loading = false;
        state.user_proposals = action.payload;
      })
      .addCase(fetchUserProposals.rejected, (state, action) => {
        state.user_proposals_loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSelectedProposal, setUserVote } = proposalsSlice.actions;
export default proposalsSlice.reducer;